import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDocs, getDoc, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmModal } from '../../components/ConfirmModal';

// ── Utilities ─────────────────────────────────────────────────────────────────

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function fmtDate(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function cleanError(e: any): string {
  const msg = e?.message || String(e);
  return msg.replace(/^Firebase: /, '').replace(/ \(functions\/[^)]+\)\.?$/, '');
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface SequenceStep {
  stepNumber: number;
  daysAfterPrevious: number;
  subject: string;
  body: string;
}

interface ImportResult {
  contactsCreated: number;
  contactsSkipped: number;
  skipReasons: string[];
}

interface Campaign {
  name: string;
  status: string;
  contactCount: number | null;
  createdAt: any;
  sequenceSteps: SequenceStep[];
}

interface Contact {
  id: string;
  email: string;
  firstName: string;
  school: string;
  segment: string;
  sequenceStep: number;
  status: string;
  nextSendAt: any;
  lastSentAt: any;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_STEPS: SequenceStep[] = [
  { stepNumber: 1, daysAfterPrevious: 0, subject: '', body: '' },
  { stepNumber: 2, daysAfterPrevious: 5, subject: '', body: '' },
  { stepNumber: 3, daysAfterPrevious: 5, subject: '', body: '' },
  { stepNumber: 4, daysAfterPrevious: 5, subject: '', body: '' },
];

const CAMPAIGN_STATUS_STYLES: Record<string, React.CSSProperties> = {
  draft:     { background: 'var(--info-dim)', color: 'var(--info)' },
  active:    { background: 'rgba(0,232,122,0.1)', color: 'var(--accent-text)' },
  paused:    { background: 'var(--warning-dim)', color: 'var(--warning)' },
  completed: { background: 'rgba(85,85,106,0.2)', color: 'var(--text-muted)' },
};

const CONTACT_STATUS_STYLES: Record<string, React.CSSProperties> = {
  active:       { background: 'rgba(0,232,122,0.1)', color: 'var(--accent-text)' },
  paused:       { background: 'var(--warning-dim)', color: 'var(--warning)' },
  unsubscribed: { background: 'var(--error-dim)', color: 'var(--error)' },
  replied:      { background: 'var(--info-dim)', color: 'var(--info)' },
  completed:    { background: 'rgba(85,85,106,0.2)', color: 'var(--text-muted)' },
};

const PAGE_SIZE = 25;
const MANUAL_STATUSES = ['active', 'paused', 'unsubscribed'];

// ── Component ─────────────────────────────────────────────────────────────────

export function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Templates editor
  const [steps, setSteps] = useState<SequenceStep[]>(DEFAULT_STEPS);
  const [stepsSaving, setStepsSaving] = useState(false);
  const [stepsError, setStepsError] = useState('');
  const [stepsSuccess, setStepsSuccess] = useState(false);

  // Activation
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState('');

  // Pause / resume
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState('');

  // Save as template
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [saveTemplateError, setSaveTemplateError] = useState('');
  const [pendingOverwrite, setPendingOverwrite] = useState(false);

  // Import more contacts
  const importFileRef = useRef<HTMLInputElement>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importCsvFile, setImportCsvFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Contacts table
  const [contactPage, setContactPage] = useState(1);
  const [contactSearch, setContactSearch] = useState('');
  const [updatingContactId, setUpdatingContactId] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !campaignId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, campaignId]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [campaignSnap, contactsSnap] = await Promise.all([
        getDoc(doc(db, 'campaigns', campaignId!)),
        getDocs(query(
          collection(db, 'campaigns', campaignId!, 'contacts'),
          orderBy('email'),
        )),
      ]);

      if (!campaignSnap.exists()) {
        setError('Campaign not found.');
        setLoading(false);
        return;
      }

      const c = campaignSnap.data();
      const loaded: Campaign = {
        name:          c.name ?? '',
        status:        c.status ?? 'draft',
        contactCount:  c.contactCount ?? null,
        createdAt:     c.createdAt,
        sequenceSteps: c.sequenceSteps ?? [],
      };
      setCampaign(loaded);
      setSteps(loaded.sequenceSteps.length > 0 ? loaded.sequenceSteps : DEFAULT_STEPS);

      setContacts(contactsSnap.docs.map(d => {
        const data = d.data();
        return {
          id:           d.id,
          email:        data.email ?? '',
          firstName:    data.firstName ?? '',
          school:       data.school ?? '',
          segment:      data.segment ?? '',
          sequenceStep: data.sequenceStep ?? 0,
          status:       data.status ?? 'active',
          nextSendAt:   data.nextSendAt,
          lastSentAt:   data.lastSentAt,
        };
      }));
    } catch (e: any) {
      setError(e.message || 'Failed to load campaign.');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplates = async () => {
    setStepsError('');
    const emptySteps = steps.filter(s => !s.subject.trim() || !s.body.trim());
    if (emptySteps.length > 0) {
      setStepsError(`Steps ${emptySteps.map(s => s.stepNumber).join(', ')} have empty subject or body.`);
      return;
    }
    setStepsSaving(true);
    setStepsSuccess(false);
    try {
      await httpsCallable(getFunctions(), 'updateCollegeOutreachTemplates')({ campaignId, steps });
      setCampaign(prev => prev ? { ...prev, sequenceSteps: steps } : prev);
      setStepsSuccess(true);
      setTimeout(() => setStepsSuccess(false), 3000);
    } catch (e: any) {
      setStepsError(cleanError(e));
    } finally {
      setStepsSaving(false);
    }
  };

  const activate = async () => {
    setActivating(true);
    setActivateError('');
    try {
      await httpsCallable(getFunctions(), 'activateCollegeOutreachCampaignCallable')({ campaignId });
      setCampaign(prev => prev ? { ...prev, status: 'active' } : prev);
    } catch (e: any) {
      setActivateError(cleanError(e));
    } finally {
      setActivating(false);
    }
  };

  const togglePause = async () => {
    if (!campaign) return;
    setToggling(true);
    setToggleError('');
    try {
      const { data } = await httpsCallable<any, { status: string }>(
        getFunctions(), 'pauseCollegeOutreachCampaign'
      )({ campaignId });
      setCampaign(prev => prev ? { ...prev, status: data.status } : prev);
    } catch (e: any) {
      setToggleError(cleanError(e));
    } finally {
      setToggling(false);
    }
  };

  const saveAsTemplate = async (overwrite = false) => {
    setSaveTemplateError('');
    setSavingTemplate(true);
    try {
      const { data } = await httpsCallable<any, { alreadyExists: boolean; templateId: string }>(
        getFunctions(), 'saveOutreachTemplate'
      )({ name: templateName.trim(), sequenceSteps: steps, overwrite });
      if (data.alreadyExists) {
        setPendingOverwrite(true);
        setSavingTemplate(false);
        return;
      }
      setShowSaveModal(false);
      setTemplateName('');
      setPendingOverwrite(false);
    } catch (e: any) {
      setSaveTemplateError(cleanError(e));
    } finally {
      setSavingTemplate(false);
    }
  };

  const importMore = async () => {
    if (!importCsvFile) return;
    setImporting(true);
    setImportError('');
    try {
      const csvText = await readFileAsText(importCsvFile);
      const { data } = await httpsCallable<any, ImportResult>(
        getFunctions(), 'importMoreCollegeOutreachContacts'
      )({ campaignId, csvText });
      setImportResult(data);
      await load();
    } catch (e: any) {
      setImportError(cleanError(e));
    } finally {
      setImporting(false);
    }
  };

  const updateContactStatus = async (contactId: string, newStatus: string) => {
    setUpdatingContactId(contactId);
    try {
      await httpsCallable(getFunctions(), 'updateCollegeOutreachContactStatus')(
        { campaignId, contactId, status: newStatus }
      );
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, status: newStatus } : c));
    } catch (e: any) {
      console.error('updateCollegeOutreachContactStatus failed:', cleanError(e));
    } finally {
      setUpdatingContactId(null);
    }
  };

  // ── Contacts filtering + pagination ──────────────────────────────────────────
  const q = contactSearch.trim().toLowerCase();
  const filteredContacts = q
    ? contacts.filter(c =>
        c.email.toLowerCase().includes(q) ||
        c.firstName.toLowerCase().includes(q) ||
        c.school.toLowerCase().includes(q))
    : contacts;
  const totalContactPages = Math.max(1, Math.ceil(filteredContacts.length / PAGE_SIZE));
  const safePage  = Math.min(contactPage, totalContactPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageContacts = filteredContacts.slice(pageStart, pageStart + PAGE_SIZE);

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return <Layout><div className="loading">Loading…</div></Layout>;
  }

  if (error) {
    return (
      <Layout>
        <button className="back-btn" onClick={() => navigate('/outreach')}>← Campaigns</button>
        <div className="error-message">{error}</div>
      </Layout>
    );
  }

  if (!campaign) return null;

  const canActivate    = campaign.status === 'draft' && campaign.sequenceSteps.length > 0;
  const showPauseResume = campaign.status === 'active' || campaign.status === 'paused';

  return (
    <Layout>
      <button className="back-btn" onClick={() => navigate('/outreach')}>← Campaigns</button>

      {/* ── 1. Campaign header ───────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between', gap: 24, flexWrap: 'wrap',
        }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>{campaign.name}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span
                className="badge"
                style={CAMPAIGN_STATUS_STYLES[campaign.status] ?? CAMPAIGN_STATUS_STYLES.draft}
              >
                {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
              </span>
              {campaign.contactCount !== null && (
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {campaign.contactCount.toLocaleString()} contacts
                </span>
              )}
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Created {fmtDate(campaign.createdAt)}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {showPauseResume && (
              <button
                className="btn-secondary"
                onClick={togglePause}
                disabled={toggling}
                style={{ fontSize: 13, padding: '7px 16px' }}
              >
                {toggling
                  ? 'Updating…'
                  : campaign.status === 'active' ? 'Pause campaign' : 'Resume campaign'}
              </button>
            )}
            {canActivate && (
              <button
                className="btn-primary"
                onClick={activate}
                disabled={activating}
                style={{ fontSize: 13, padding: '7px 16px' }}
              >
                {activating ? 'Activating…' : 'Activate campaign'}
              </button>
            )}
          </div>
        </div>

        {activateError && (
          <div className="error-message" style={{ marginTop: 14 }}>{activateError}</div>
        )}
        {toggleError && (
          <div className="error-message" style={{ marginTop: 14 }}>{toggleError}</div>
        )}
      </div>

      {/* ── 2. Sequence templates ────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="page-header" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>Sequence Templates</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {stepsSuccess && (
              <span style={{ fontSize: 13, color: 'var(--accent-text)', fontWeight: 500 }}>
                Saved ✓
              </span>
            )}
            <button
              className="btn-secondary"
              onClick={() => { setTemplateName(''); setSaveTemplateError(''); setPendingOverwrite(false); setShowSaveModal(true); }}
              style={{ fontSize: 13, padding: '7px 16px' }}
            >
              Save as template
            </button>
            <button
              className="btn-primary"
              onClick={saveTemplates}
              disabled={stepsSaving}
              style={{ fontSize: 13, padding: '7px 16px' }}
            >
              {stepsSaving ? 'Saving…' : 'Save templates'}
            </button>
          </div>
        </div>

        {stepsError && (
          <div className="error-message" style={{ marginBottom: 16 }}>{stepsError}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {steps.map((step, idx) => (
            <div
              key={step.stepNumber}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 700, flexShrink: 0 }}>Step {step.stepNumber}</span>
                {idx === 0 ? (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sends immediately on activation</span>
                ) : (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                    <input
                      type="number"
                      min={1}
                      className="form-input"
                      value={step.daysAfterPrevious}
                      onChange={e => setSteps(prev => prev.map((s, i) =>
                        i === idx ? { ...s, daysAfterPrevious: Math.max(1, parseInt(e.target.value, 10) || 1) } : s
                      ))}
                      style={{ width: 52, padding: '2px 6px', fontSize: 12, textAlign: 'center' }}
                    />
                    days after previous
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => setSteps(prev =>
                    prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepNumber: i + 1 }))
                  )}
                  disabled={steps.length === 1}
                  style={{
                    marginLeft: 'auto', fontSize: 12, color: 'var(--error)',
                    background: 'none', border: 'none', padding: '2px 6px',
                    cursor: steps.length === 1 ? 'not-allowed' : 'pointer',
                    opacity: steps.length === 1 ? 0.35 : 1,
                  }}
                >
                  Remove
                </button>
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Subject</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Email subject line"
                  value={step.subject}
                  onChange={e => setSteps(prev => prev.map((s, i) =>
                    i === idx ? { ...s, subject: e.target.value } : s
                  ))}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Body</label>
                <textarea
                  className="form-input"
                  placeholder={`Email body — use {{firstName}}, {{school}} for merge tags`}
                  value={step.body}
                  rows={6}
                  onChange={e => setSteps(prev => prev.map((s, i) =>
                    i === idx ? { ...s, body: e.target.value } : s
                  ))}
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setSteps(prev => [
            ...prev,
            { stepNumber: prev.length + 1, daysAfterPrevious: 5, subject: '', body: '' },
          ])}
          style={{ marginTop: 4, fontSize: 13, padding: '7px 16px' }}
        >
          + Add Step
        </button>
      </div>

      {/* ── 3. Contacts table ────────────────────────────────────────────────── */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>
            Contacts
            {contacts.length > 0 && (
              <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>
                ({contacts.length.toLocaleString()})
              </span>
            )}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="btn-secondary"
              onClick={() => {
                setImportResult(null); setImportCsvFile(null);
                setImportFileName(''); setImportError('');
                setShowImportModal(true);
              }}
              style={{ fontSize: 13, padding: '7px 16px', flexShrink: 0 }}
            >
              Import more contacts
            </button>
            <input
              className="form-input"
              type="text"
              placeholder="Search by email, name, or school…"
              value={contactSearch}
              onChange={e => { setContactSearch(e.target.value); setContactPage(1); }}
              style={{ width: 280 }}
            />
          </div>
        </div>

        {contacts.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
            No contacts imported yet.
          </div>
        ) : filteredContacts.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No contacts match "{contactSearch}".
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>School</th>
                    <th>Segment</th>
                    <th>Step</th>
                    <th>Status</th>
                    <th>Next send</th>
                    <th>Last sent</th>
                  </tr>
                </thead>
                <tbody>
                  {pageContacts.map(contact => {
                    const isManual   = MANUAL_STATUSES.includes(contact.status);
                    const isUpdating = updatingContactId === contact.id;
                    return (
                      <tr key={contact.id}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{contact.email}</td>
                        <td style={{ fontSize: 13 }}>{contact.firstName || '—'}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{contact.school || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{contact.segment || '—'}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                          {contact.sequenceStep} / {campaign.sequenceSteps.length || '?'}
                        </td>
                        <td>
                          {isManual ? (
                            <select
                              className="form-input"
                              value={contact.status}
                              disabled={isUpdating}
                              onChange={e => updateContactStatus(contact.id, e.target.value)}
                              style={{ padding: '3px 8px', width: 'auto', fontSize: 12 }}
                            >
                              {MANUAL_STATUSES.map(s => (
                                <option key={s} value={s}>
                                  {s.charAt(0).toUpperCase() + s.slice(1)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span
                              className="badge"
                              style={CONTACT_STATUS_STYLES[contact.status] ?? {}}
                            >
                              {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {fmtDateTime(contact.nextSendAt)}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {fmtDateTime(contact.lastSentAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredContacts.length > PAGE_SIZE && (
              <div style={{
                padding: '12px 24px',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
              }}>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredContacts.length)} of {filteredContacts.length.toLocaleString()}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn-secondary"
                    style={{ padding: '5px 14px', fontSize: 13 }}
                    disabled={safePage === 1}
                    onClick={() => setContactPage(safePage - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="btn-secondary"
                    style={{ padding: '5px 14px', fontSize: 13 }}
                    disabled={safePage === totalContactPages}
                    onClick={() => setContactPage(safePage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* ── Import more contacts modal ───────────────────────────────────────── */}
      {showImportModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div className="card" style={{ width: 460, padding: 28 }}>
            {importResult ? (
              <>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>Import complete</h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: importResult.contactsSkipped > 0 ? '1fr 1fr' : '1fr',
                  gap: 20, marginBottom: 20,
                }}>
                  <div>
                    <div className="stat-label">Contacts added</div>
                    <div className="stat-value" style={{ fontSize: 26, marginTop: 4 }}>
                      {importResult.contactsCreated.toLocaleString()}
                    </div>
                  </div>
                  {importResult.contactsSkipped > 0 && (
                    <div>
                      <div className="stat-label">Rows skipped</div>
                      <div className="stat-value" style={{ fontSize: 26, marginTop: 4, color: 'var(--warning)' }}>
                        {importResult.contactsSkipped.toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
                {importResult.skipReasons.length > 0 && (
                  <div style={{ marginBottom: 20, maxHeight: 140, overflowY: 'auto' }}>
                    {importResult.skipReasons.map((r, i) => (
                      <div key={i} style={{
                        fontSize: 12, color: 'var(--text-muted)',
                        fontFamily: 'monospace', padding: '2px 0',
                      }}>{r}</div>
                    ))}
                  </div>
                )}
                <button
                  className="btn-primary"
                  onClick={() => setShowImportModal(false)}
                  style={{ fontSize: 13, padding: '7px 16px' }}
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>Import more contacts</h3>
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Contact List (CSV)</label>
                  <div
                    style={{
                      border: `1px dashed ${importFileName ? 'var(--accent)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius-md)', padding: '20px 16px', textAlign: 'center',
                      cursor: 'pointer',
                      background: importFileName ? 'var(--accent-tint)' : 'var(--bg-secondary)',
                    }}
                    onClick={() => importFileRef.current?.click()}
                  >
                    <input
                      ref={importFileRef}
                      type="file"
                      accept=".csv,text/csv"
                      style={{ display: 'none' }}
                      onChange={e => {
                        const f = e.target.files?.[0] ?? null;
                        setImportCsvFile(f); setImportFileName(f?.name ?? '');
                      }}
                    />
                    {importFileName ? (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--accent-text)' }}>{importFileName}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Click to change</div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>Click to upload a CSV file</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7 }}>
                          Required: <code>email</code>, <code>school</code> — <code>firstName</code> and <code>segment</code> are optional
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {importError && (
                  <div className="error-message" style={{ marginBottom: 14 }}>{importError}</div>
                )}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button
                    className="btn-secondary"
                    onClick={() => setShowImportModal(false)}
                    style={{ fontSize: 13, padding: '7px 16px' }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={importMore}
                    disabled={importing || !importCsvFile}
                    style={{ fontSize: 13, padding: '7px 16px' }}
                  >
                    {importing ? 'Importing…' : 'Import'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Save-as-template modal ────────────────────────────────────────────── */}
      {showSaveModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div className="card" style={{ width: 420, padding: 28 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>Save as template</h3>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Template name</label>
              <input
                className="form-input"
                type="text"
                placeholder="e.g. Standard 4-step outreach"
                value={templateName}
                autoFocus
                onChange={e => setTemplateName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && templateName.trim()) saveAsTemplate(); }}
              />
            </div>
            {saveTemplateError && (
              <div className="error-message" style={{ marginBottom: 14 }}>{saveTemplateError}</div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={() => setShowSaveModal(false)}
                style={{ fontSize: 13, padding: '7px 16px' }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => saveAsTemplate()}
                disabled={savingTemplate || !templateName.trim()}
                style={{ fontSize: 13, padding: '7px 16px' }}
              >
                {savingTemplate ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingOverwrite && (
        <ConfirmModal
          title="Overwrite template?"
          message={`A template named "${templateName}" already exists. Replace it with the current steps?`}
          confirmLabel="Overwrite"
          cancelLabel="Cancel"
          onConfirm={() => { setPendingOverwrite(false); saveAsTemplate(true); }}
          onCancel={() => setPendingOverwrite(false)}
        />
      )}
    </Layout>
  );
}
